import React from 'react';
import { cn } from '../lib/utils';

/**
 * Card — surface container with the project's signature border.
 * Uses `border border-border` (hairline gray) on `bg-surface` (off-white).
 *
 * Variants: default (off-white) | elevated (tinted) | ghost | brand
 * Sizes: default | sm — sm has tighter padding for compact stat cards.
 */
export function Card({ className, variant = 'default', size = 'default', children, ...props }) {
  const variants = {
    default: 'bg-surface text-fg border border-border',
    elevated: 'bg-surface-2 text-fg border border-border-strong',
    ghost: 'bg-transparent text-fg border border-border',
    brand: 'bg-surface text-fg border border-brand/30',
  };
  const sizes = {
    default: 'rounded-lg overflow-hidden',
    sm: 'rounded-md overflow-hidden',
  };
  return (
    <div
      data-slot="card"
      data-size={size}
      className={cn(
        'flex flex-col',
        variants[variant],
        sizes[size],
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

function Header({ className, children, ...props }) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        'flex items-start justify-between gap-4 p-5 pb-3',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

function Title({ className, children, ...props }) {
  return (
    <h3
      data-slot="card-title"
      className={cn('text-base font-semibold leading-snug text-fg', className)}
      {...props}
    >
      {children}
    </h3>
  );
}

function Subtitle({ className, children, ...props }) {
  return (
    <p
      data-slot="card-subtitle"
      className={cn('text-xs text-fg-muted mt-1', className)}
      {...props}
    >
      {children}
    </p>
  );
}

function Body({ className, children, ...props }) {
  return (
    <div
      data-slot="card-body"
      className={cn('p-5 pt-3', className)}
      {...props}
    >
      {children}
    </div>
  );
}

function Footer({ className, children, ...props }) {
  return (
    <div
      data-slot="card-footer"
      className={cn(
        'flex flex-col gap-2 p-4 border-t border-border bg-surface-2/30',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

Card.Header = Header;
Card.Title = Title;
Card.Subtitle = Subtitle;
Card.Body = Body;
Card.Footer = Footer;
